/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { listApiKeys, createApiKey, showApiKey, deleteApiKey, updateApiKey } from "@/services/apiKey";
import { IApiKey } from "@/interfaces/apiKey";
import { useEntityNavigation } from "@/hooks/useEntityNavigation";
import useMounted from "@/hooks/mounted";
import { debounce } from "lodash";
import { useEntityQueryParams } from "@/hooks/useEntityQueryParams";
import { useEntitySearch } from "@/hooks/useEntitySearch";

const DEFAULT_DATA: IApiKey = {
  id: 0,
  name: "",
  description: "",
  expirationDate: undefined,
  lastUsedAt: undefined,
};

export const useApiKeys = ({
  disabledShowId,
  disabledGetAll,
  defaultLimit,
  filter: filterInitial,
  includes = [],
}: {
  disabledShowId?: boolean;
  disabledGetAll?: boolean;
  defaultLimit?: number;
  filter?: Record<string, any>;
  includes?: string[];
}) => {
  const { idApiKey: id } = useParams<{ idApiKey: string }>();
  const location = useLocation();
  const [apiKeys, setApiKeys] = useState<IApiKey[]>([]);
  const [apiKey, setApiKey] = useState<IApiKey>(DEFAULT_DATA);
  const { filter, handleFilter, sort, handleSort, page, setPage, limit, setLimit } =
    useEntityQueryParams({ filter: filterInitial, defaultLimit: defaultLimit ?? 25 });
     // 1-based
  const [totalItems, setTotalItems] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(false);
  const [createdApiKey, setCreatedApiKey] = useState<{ key: string; warning: string } | null>(null);
  const navigate = useNavigate();
  const { goAdd, goEdit, goBack, pathBack } = useEntityNavigation({ basePath: "/global/apikeys" });

  const handleApiKey = <K extends keyof IApiKey>(key: K, value: IApiKey[K]) => {
    setApiKey((curr) => ({ ...curr, [key]: value }));
  };

  const closeCreatedKeyModal = () => {
    setCreatedApiKey(null);
    navigate(pathBack);
  };

  const getAll = useCallback(debounce(({
    page = 1,
    limit,
    filter,
    sort
  }: {
    page?: number;
    limit?: number;
    filter?: any
    sort?: any
  }) => {
    setLoading(true);
    listApiKeys({ includes: [...includes], filter, page, limit, sort })
      .then(({ apiKeys: listApiKeys, limit, currentPage, totalItems }) => {
        setApiKeys(listApiKeys);
        setPage(currentPage);
        setLimit(limit);
        setTotalItems(totalItems)
      })
      .finally(() => setLoading(false));
  }, 300), []);

  const search = useEntitySearch({ list: listApiKeys, key: "apiKeys", includes, filter });


  const get = ({ id }: { id: string | number }) => {
    setLoading(true);
    showApiKey({ id })
      .then(({ apiKey: apiKeyShow }) => setApiKey(apiKeyShow))
      .catch(() => {
        toast.error("Não foi possível carregar a chave de API.");
      })
      .finally(() => setLoading(false));
  };

  const create = () => {
    setLoading(true);
    createApiKey({ apiKey })
      .then((response) => {
        setCreatedApiKey({
          key: response.apiKey.key,
          warning: response.warning || "Guarde esta chave em local seguro. Ela não será exibida novamente."
        });
        console.log("response", response.apiKey.key, response.warning);
        toast.success("Chave de API criada com sucesso!");
      })
      .catch(() => {
        toast.error("Falha ao criar chave de API.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const update = () => {
    setLoading(true);
    updateApiKey({ id: apiKey.id, apiKey })
      .then(() => {
        toast.success("Chave de API atualizada com sucesso!");
        navigate(pathBack);
      })
      .catch(() => {
        toast.error("Falha ao atualizar chave de API.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const deleteApiKeys = async (ids: (string | number)[]) => {
    setLoading(true);
    try {
      await Promise.all(ids.map((id) => deleteApiKey(id)));
      toast.success("Chave(s) de API deletada(s) com sucesso!");
      getAll({ page, limit });
    } catch {
      toast.error("Falha ao deletar chave(s) de API.");
    } finally {
      setLoading(false);
    }
  };

  // efeitos
  useMounted(() => {
    if (!disabledGetAll) getAll({ page, limit, filter, sort })
  }, [page, limit, filter, sort]);

  useEffect(() => {
    const isPage = location.pathname.includes("/global/apikeys");
    const targetId = id;

    if (targetId && (isPage || id) && !disabledShowId) {
      if (targetId !== "new") get({ id: targetId });
      setApiKey(DEFAULT_DATA);
      return;
    }

    if (!disabledGetAll) getAll({ limit });
  }, [id, disabledGetAll, disabledShowId, createdApiKey]);

  return {
    // estado
    apiKeys,
    apiKey,
    totalItems,
    page,
    limit,
    loading,
    id,
    createdApiKey,

    // setters
    setPage,
    setLimit,
    handleApiKey,

    // ações
    create,
    update,
    deleteApiKeys,

    // util
    search,
    handleFilter,
    handleSort,

    // navegação
    goEdit,
    goBack,
    goAdd,
    closeCreatedKeyModal,
  };
};
